import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { IoMenu, IoArrowBack, IoSunnyOutline, IoMoonOutline } from "react-icons/io5";
import { APP_VERSION, APP_BUILD } from "../../config/version";
import Wordmark from "./Wordmark";
import AccountMenu from "./AccountMenu";
import { useTheme } from "../../contexts/ThemeContext";

const MenuIcon = IoMenu as any;
const BackIcon = IoArrowBack as any;
const SunIcon = IoSunnyOutline as any;
const MoonIcon = IoMoonOutline as any;

// Two looks, one component: the home page shows the full brand (mark + wordmark);
// every other page shows the wordmark as a breadcrumb beside its own title.
type NavBarProps = (
  | { variant: "brand"; title?: never }
  | { variant?: "page"; title: string }
) & {
  showBackButton?: boolean;
  /** Optional explicit fallback target. If omitted, the back button uses browser history. */
  backTo?: string;
  /** Router state handed to `backTo`, for a page that needs to be restored on the way back. */
  backState?: unknown;
  showMenuButton?: boolean;
  onMenuClick?: () => void;
};

const NavBar: React.FC<NavBarProps> = ({
  variant = "page",
  title,
  showBackButton = false,
  backTo,
  backState,
  showMenuButton = false,
  onMenuClick,
}) => {
  const { isDark, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isBrand = variant === "brand";

  const handleBack = () => {
    if (backTo) {
      navigate(backTo, backState === undefined ? undefined : { state: backState });
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <header className="print:hidden sticky top-0 z-30 w-full bg-surface/85 backdrop-blur-md border-b border-paper-300/70">
      <div className="mx-auto flex h-16 max-w-none items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 2xl:px-10">
        {/* Left cluster */}
        <div className="flex min-w-0 items-center gap-2">
          {showMenuButton && (
            <button
              onClick={onMenuClick}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-muted hover:bg-paper-200 hover:text-ink transition-colors duration-200 lg:hidden"
              aria-label="Open menu"
            >
              <MenuIcon className="h-5 w-5" />
            </button>
          )}
          {showBackButton && (
            <button
              onClick={handleBack}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-muted hover:bg-paper-200 hover:text-ink transition-colors duration-200"
              aria-label="Back"
            >
              <BackIcon className="h-5 w-5" />
            </button>
          )}
          <Link
            to="/"
            className="ml-1 flex items-center gap-2 truncate text-xl font-bold focus:outline-none rounded"
          >
            {isBrand ? (
              <Wordmark className="text-2xl" />
            ) : (
              <>
                <Wordmark className="text-lg" withMark={false} />
                <span className="hidden md:inline-block h-5 w-px bg-paper-300" />
                <span className="hidden md:inline-block truncate text-base font-medium text-ink-muted">
                  {title}
                </span>
              </>
            )}
          </Link>
        </div>

        {/* Right cluster */}
        <div className="flex flex-shrink-0 items-center gap-2 sm:gap-3">
          <button
            onClick={toggleTheme}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            title={isDark ? "Light mode" : "Dark mode"}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-paper-300 text-ink-muted transition-colors duration-200 hover:bg-paper-200 hover:text-ink"
          >
            {isDark ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
          </button>
          <span
            className="hidden sm:inline-flex items-center rounded-full bg-paper-200 px-2.5 py-1 font-mono text-[11px] text-ink-subtle"
            title={APP_BUILD ? `App version · build ${APP_BUILD}` : "App version"}
          >
            v{APP_VERSION}{APP_BUILD ? ` · ${APP_BUILD}` : ""}
          </span>
          <AccountMenu />
        </div>
      </div>
    </header>
  );
};

export default NavBar;
